import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Input, Modal, Select } from 'antd';
import PropTypes from 'prop-types';
import { updateTherapy } from '../../api/therapiesApi';
import { getTherapyTypeLabel } from '../../utils/therapyPresentation';

const statusOptions = [
  { value: 'current', label: 'Aktuelna' },
  { value: 'past', label: 'Prosla' },
];

const UpdateTherapyStatusModal = ({ isOpen, onClose, therapy, onUpdated }) => {
  const [status, setStatus] = useState('current');
  const [medications, setMedications] = useState([]);
  const [medicationInput, setMedicationInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      setStatus(therapy?.status || 'current');
      setMedications(
        Array.isArray(therapy?.medications) ? therapy.medications : []
      );
      setMedicationInput('');
      setError('');
      setSuccessMessage('');
      setDone(false);
      setLoading(false);
    }
  }, [isOpen, therapy?._id, therapy?.status, therapy?.medications]);

  const handleAddMedication = useCallback(() => {
    const raw = medicationInput?.trim();
    if (!raw) return;
    const items = raw
      .split(',')
      .map((m) => m.trim())
      .filter(Boolean);
    if (!items.length) return;
    setMedications((prev) => [...prev, ...items]);
    setMedicationInput('');
  }, [medicationInput]);

  const handleRemoveMedication = useCallback((index) => {
    setMedications((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleOk = useCallback(async () => {
    if (!therapy?._id) {
      setError('Terapija nije izabrana.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    const {
      ok,
      status: httpStatus,
      message,
    } = await updateTherapy(therapy._id, { status, medications });

    if (!ok) {
      setError(httpStatus ? message : message);
      setLoading(false);
      return;
    }

    setSuccessMessage(message || 'Status terapije azuriran.');
    setDone(true);
    onUpdated?.();
    setTimeout(() => {
      setDone(false);
      onClose();
    }, 800);
    setLoading(false);
  }, [medications, onClose, onUpdated, status, therapy?._id]);

  const handleCancel = () => {
    setError('');
    setSuccessMessage('');
    setDone(false);
    onClose();
  };

  return (
    <Modal
      className="therapy_status_modal"
      title="Azuriraj terapiju"
      open={isOpen}
      onOk={handleOk}
      onCancel={handleCancel}
      footer={[
        <Button
          key="save"
          type="primary"
          onClick={handleOk}
          disabled={loading || done}
          style={{ width: '150px' }}
        >
          {loading ? (
            <img
              src="/assets/icons/add.svg"
              alt="Učitavanje"
              className="animate-spin"
            />
          ) : done ? (
            <img
              src="/assets/icons/check.svg"
              alt="Done"
              className="check-icon"
            />
          ) : (
            'Sacuvaj'
          )}
        </Button>,
      ]}
    >
      {therapy?._id && (
        <div style={{ marginBottom: 12 }}>
          <strong>Tip:</strong> {getTherapyTypeLabel(therapy)}
        </div>
      )}
      <Select
        style={{ width: '100%' }}
        value={status}
        onChange={(value) => setStatus(value)}
        options={statusOptions}
      />
      <div style={{ marginTop: 12 }}>
        <div className="therapy-med-input-row">
          <Input
            placeholder="Unesite lek pa kliknite +"
            value={medicationInput}
            onChange={(e) => setMedicationInput(e.target.value)}
            onPressEnter={(e) => {
              e.preventDefault();
              handleAddMedication();
            }}
          />
          <Button type="primary" onClick={handleAddMedication}>
            +
          </Button>
        </div>
        <div className="therapy-med-list">
          {medications.map((med, index) => (
            <div key={`${med}-${index}`} className="therapy-med-chip">
              <span>{med}</span>
              <button
                type="button"
                className="therapy-med-remove"
                onClick={() => handleRemoveMedication(index)}
              >
                x
              </button>
            </div>
          ))}
        </div>
      </div>
      {successMessage && (
        <Alert
          title={successMessage}
          type="success"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}
      {error && (
        <Alert title={error} type="error" showIcon style={{ marginTop: 16 }} />
      )}
    </Modal>
  );
};

UpdateTherapyStatusModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  therapy: PropTypes.object,
  onUpdated: PropTypes.func,
};

export default UpdateTherapyStatusModal;
